import { ref, Ref } from 'vue';
import type { Swiper } from 'swiper';

interface Bullet {
  active: boolean;
}

interface SwiperConfig {
  slidesPerView?: number;
  spaceBetween?: number;
  slidesPerGroup?: number;
  pagination?: boolean | object;
  navigation?: boolean;
  breakpoints?: object;
}

export function useSwiper(initialConfig?: SwiperConfig) {
  const bullets: Ref<Bullet[]> = ref([]);
  const swiperInstance = ref();

  const defaultConfig: SwiperConfig = {
    slidesPerView: 1,
    spaceBetween: 30,
    pagination: false,
  };

  const swiperConfig = ref<SwiperConfig>({
    ...defaultConfig,
    ...initialConfig,
  });

  function calculateBullets(swiper: Swiper) {
    const slidesPerGroup = swiper.params.slidesPerGroup || 1;
    const totalSlides = swiper.slides.length;

    if (slidesPerGroup > 1) {
      // Page-based bullets
      const totalPages = Math.ceil(totalSlides / slidesPerGroup);
      const isGroupNavigation =
        swiper.snapGrid && swiper.snapGrid.length <= totalPages;

      let currentPage = 0;
      if (isGroupNavigation && swiper.snapGrid) {
        // True group navigation - use snapIndex to determine current page
        currentPage = swiper.snapIndex || 0;
      } else {
        // Slide-by-slide navigation - determine page by which group contains active slide
        currentPage = Math.floor(swiper.realIndex / slidesPerGroup);
      }

      // Only recreate bullets array if the count changed (e.g., on breakpoint)
      if (bullets.value.length !== totalPages) {
        bullets.value = [];
        for (let i = 0; i < totalPages; i++) {
          bullets.value.push({
            active: i === currentPage,
          });
        }
      } else {
        // Just update the active state
        bullets.value.forEach((bullet, index) => {
          bullet.active = index === currentPage;
        });
      }
    } else {
      // Slide-based bullets
      if (bullets.value.length !== totalSlides) {
        bullets.value = [];
        for (let i = 0; i < totalSlides; i++) {
          bullets.value.push({
            active: i === swiper.realIndex,
          });
        }
      } else {
        // Just update the active state
        bullets.value.forEach((bullet, index) => {
          bullet.active = index === swiper.realIndex;
        });
      }
    }
  }

  function onInit(event: CustomEvent) {
    const swiper = event.detail[0];
    calculateBullets(swiper);
  }

  function onSlideChange(event: CustomEvent) {
    const swiper = event.detail[0];
    calculateBullets(swiper);
  }

  function onBreakpoint(event: CustomEvent) {
    const swiper = event.detail[0];
    calculateBullets(swiper);
  }

  function goToSlide(index: number) {
    if (swiperInstance.value && swiperInstance.value.swiper) {
      const swiper = swiperInstance.value.swiper;
      const slidesPerGroup = swiper.params.slidesPerGroup || 1;

      if (slidesPerGroup > 1) {
        // Go to the first slide of that page
        const targetSlide = index * slidesPerGroup;
        swiper.slideTo(targetSlide);
      } else {
        swiper.slideTo(index);
      }
    }
  }

  function nextSlide() {
    if (swiperInstance.value) {
      swiperInstance.value.swiper.slideNext();
    }
  }

  function prevSlide() {
    if (swiperInstance.value) {
      swiperInstance.value.swiper.slidePrev();
    }
  }

  return {
    bullets,
    swiperInstance,
    swiperConfig,
    onInit,
    onSlideChange,
    onBreakpoint,
    goToSlide,
    nextSlide,
    prevSlide,
  };
}
